import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {isEmpty, isEqual} from "lodash";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import resourceService from "@plannel/services/data/resource-service";
import wipService from "@plannel/services/mp/wip-service";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import PaginationContainer from "@plannel/components/PaginationContainer";
import DefaultGridSetting, { gridPaste, nullSafeComparator} from "@plannel/components/aggrid/DefaultGridSetting";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import Snackbar from "@plannel/components/Snackbar";

import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import "@plannel/scss/components/CustomGrid.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import {AddButton, FilterButton, RemoveButton, SaveButton} from "@plannel/components/ActionIconButton";
import DataState from "@plannel/components/aggrid/DataState";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import routeService from "@plannel/services/data/route-service";
import GridDatePicker from "@plannel/components/GridDatePicker";
import Dialog from "@plannel/components/Dialog";
import customerService from "@plannel/services/data/customer-service";
import DateUtils from "@plannel/utils/date-util";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";

const { snackBarMessageColor } = ColorUtil;

const WIP = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [sortParams, setSortParams] = useState({});
  const [wip, setWip] = useState([]);
  const [date, setDate] = useState(reduxViewState?.date ?? new ZDate());

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");
  const [openFilter, setOpenFilter] = useState(false);

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const [resourceSearch, setResourceSearch] = useState(reduxViewState?.resourceSearch ?? null);
  // 자원 후보 목록 캐시는 resourceOptions 키에 둔다 — viewStates 의 resource 는 "선택한 자원"을 뜻한다(ResourceFilter).
  const [resource, setResource] = useState(reduxViewState?.resourceOptions ?? []);
  const resourceMap = useRef(reduxViewState?.resourceMap ?? {});
  const [route, setRoute] = useState(reduxViewState?.route ?? []);
  const routeMap = useRef(reduxViewState?.routeMap ?? {});
  const [customers, setCustomers] = useState(reduxViewState?.customers ?? []);

  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const retrieveResource = () => {
    resourceService.getAll({ pagination: false })
      .then(response => {
        const { results } = response.data;

        setResource(results);
        const map = {}

        for (const res of results) {
          map[res.resCd] = res;
        }

        resourceMap.current = map;
        if (reduxViewState) {
          reduxViewState.resourceOptions = results;
          reduxViewState.resourceMap = map;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveRoute = () => {
    routeService.getAll()
      .then(response => {
        const { results } = response.data;
        setRoute(results);
        const map = {}

        for (const res of results) {
          map[res.routeCd] = res;
        }

        routeMap.current = map;
        if (reduxViewState) {
          reduxViewState.route = results;
          reduxViewState.routeMap = map;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveCustomers = () => {
    customerService.getLookup()
      .then(response => {
        const { results } = response.data;
        setCustomers(results);
        if (reduxViewState) {
          reduxViewState.customers = results;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  const dropdownValueFormatter = (params) => {
    const v = params?.value ?? params;
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (typeof v === "object") return v.name ?? v.locNm ?? v.locName ?? "";
    return String(v);
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];

    return nullSafeComparator(val1, val2, isDescending);
  }

  const customerFormatter = (params) => {
    const customerList = params?.context?.customers ?? customers;
    const filterVal = customerList.find(arr => arr.customerCd === params.value);
    return filterVal ? filterVal.name : null;
  }

  const columnDefs = [
    {
      headerName: "locCd", field: "locationCd", colId: "locationCd",
      type: ['nonEditableColumn'],
      cellClass: 'stringType',
      width: 200, filterType: 'string'
    },
    { headerName: "locName",
      field: "locationNm",
      type: ['nonEditableColumn'], filterType: 'string',
    },
    {
      headerName: "wcCd", field: "workCenterCd",
      colId: "workcenter", type: ['nonEditableColumn'],
      width: 200,
      filterType: 'string',
    },
    { headerName: "itemCd", field: "itemCd", type: ['nonEditableColumn'], filterType: 'string' },
    { headerName: "itemName", field: "itemNm", type: ['nonEditableColumn'], filterType: 'string' },
    { headerName: "resCd", field: "resourceCd", type: ['nonEditableColumn'], cellClass: 'stringType', filterType: 'string' },
    {
      headerName: "resName", field: "resourceNm", colId: "resNm", width: 180, filterType: 'string',
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditor: "agRichSelectCellEditor",
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        valueFormatter: dropdownValueFormatter,
        values: (params) => params?.context?.resource ?? []
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
    },
    { headerName: "routeCd", colId: "routeCd", field: "routeCd", type: ['nonEditableColumn'], filterType: 'string' },
    {
      headerName: "routeName", field: "routeNm", colId: "routeNm",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        values: (params) => params?.context?.route ?? []
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150,
      filterType: 'string',
    },
    {
      headerName: "prodDate",
      field: "prodDate",
      type: ["defaultValueParser"],
      cellEditor: GridDatePicker,
      cellEditorParams: { dateType: "day", popupGrid: true },
      width: 200, filterType: "date", valueFormatter: (data) => DateUtils.formatDate(data.value, "yyyy-MM-dd")
    },
    {
      headerName: "expDate",
      field: "expDate",
      type: ["defaultValueParser"],
      cellEditor: GridDatePicker,
      cellEditorParams: { dateType: "day", popupGrid: true },
      width: 200, filterType: "date", valueFormatter: (data) => DateUtils.formatDate(data.value, "yyyy-MM-dd")
    },
    {
      headerName: "wipCd", field: "wipCd", colId: "wipCd",
      cellClass: 'stringType',
      width: 200, filterType: 'string'
    },
    { headerName: "wipQty", field: "wipQty", type: ['numberColumn'], filterType: 'number' },
    { headerName: "wipAmt", field: "wipAmt", type: ['numberColumn'], filterType: 'number' },
    { headerName: "orderNum", field: "orderNum", filterType: 'string' },
    {
      headerName: "customerCd", field: "customerCd", colId: "customerCd",
      cellClass: 'stringType',
      filterType: 'string',
      type: ['nonEditableColumn'],
    },
    {
      headerName: "customerName",
      field: "customerNm",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditorParams: {
        values: params => {
          const customerList = params?.context?.customers ?? customers;
          return customerList.map(c => c.name);
        }
      },
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: customerFormatter
      },
      comparator: (val1, val2) => val1.localeCompare(val2),
      valueSetter: (params) => {
        const selectedName = params.newValue;
        const customerList = params?.context?.customers ?? customers;
        const customer = customerList.find(c => c.name === selectedName);
        if (customer) {
          params.data.customerNm = customer.name;
          params.data.customerCd = customer.customerCd;
        } else {
          params.data.customerNm = null;
          params.data.customerCd = null;
        }
        return true;
      },
      minWidth: 150,
      flex: 2,
      filterType: 'string'
    },
    { headerName: "createdTs", field: "createdTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const defaultExportColumnDefs = [
    { headerName: "locCd", field: "locationCd", width: 140, cellClass: 'stringType' },
    { headerName: "locName", field: "locationNm", width: 140 },
    { headerName: "itemCd", field: "itemCd", width: 140, cellClass: 'stringType' },
    { headerName: "itemName", field: "itemNm", width: 140 },
    { headerName: "workcenter", field: "workCenterCd", width: 140, cellClass: 'stringType' },
    { headerName: "resCd", field: "resourceCd", width: 140, cellClass: 'stringType' },
    { headerName: "resName", field: "resourceNm", width: 140 },
    { headerName: "routeCd", field: "routeCd", width: 140 },
    { headerName: "routeNm", field: "routeNm", width: 140 },
    { headerName: "prodDate", field: "prodDate", width: 140 },
    { headerName: "expDate", field: "expDate", width: 140 },
    { headerName: "wipCd", field: "wipCd", width: 140, cellClass: 'stringType' },
    { headerName: "wipQty", field: "wipQty", width: 140 },
    { headerName: "wipAmt", field: "wipAmt", width: 140 },
    { headerName: "orderNum", field: "orderNum", width: 140 },
    { headerName: "customerCd", field: "customerCd", width: 140, cellClass: 'stringType' },
    { headerName: "customerName", field: "customerNm", width: 140 },
    { headerName: "createdTs", field: "createdTs", valueFormatter: DateUtils.formatDateTime },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: DateUtils.formatDateTime },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", filterType: 'string' }
  ];

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (date) {
      searchFilters.date = date;
    }

    if (resourceSearch) {
      searchFilters.resourceCd = resourceSearch.resCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (filterValue?.locations) {
      searchFilters.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }

      if (resultTotalCount.current) {
        params.totalCount = resultTotalCount.current;
      }
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const addRow = () => {
    const newRow = {
      id: null,
      locationCd: null,
      locationNm: null,
      itemCd: null,
      itemNm: null,
      resourceCd: null,
      resourceNm: null,
      prodDate: null,
      expDate: null,
      workCenterCd: null,
      routeCd: null,
      routeNm: null,
      wipCd: null,
      wipQty: null,
      wipAmt: null,
      orderNum: null,
      customerCd: null,
      customerNm: null,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const retrieveWip = () => {
    gridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();

    wipService.getAll(params)
      .then(response => {
        const { results, totalItems, totalPages } = response.data;
        if (!isEmpty(results)) {
          setWip(setGridData(results));
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: columnDefs });
          setWip([]);
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: columnDefs });
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { date, itemSearch: itemSearch.current, filter: filterValue, extraItemCodes: extraItemCodesRef.current, resourceSearch, currentPage, pageSize });
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: columnDefs, viewName, initState: true });
  }

  const onCellValueChanged = (params) => {
    gridPaste(params);

    const {column, node} = params;
    const colId = column.getColId();
    if (isEqual(colId, "resNm")) {
      if (!resourceMap.current[params.value?.resCd]) {
        params.data.resCd = null;
        params.data.resNm = null;
        params.data.resourceId = null;
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("resource")}] ${t("MSG_InvalidValue")}` });
        return;
      }
      const resCd = params.value?.resCd;
      node.data.resourceId = resourceMap.current[resCd].id;
      node.data.resourceCd = resourceMap.current[resCd].resCd;
      node.data.resourceNm = resourceMap.current[resCd].name;
      node.data.locationId = resourceMap.current[resCd].locId || null;
      node.data.locationNm = resourceMap.current[resCd].locName || null;
      node.data.locationCd = resourceMap.current[resCd].locCd || null;
      node.data.workCenterCd = resourceMap.current[resCd].workcenterCd || null;
      node.data.workCenterId = resourceMap.current[resCd].workcenter?.id || null;

    } else if (isEqual(colId, "routeNm")) {
      if (!routeMap.current[params.value?.routeCd]) {
        params.data.routeCd = null;
        params.data.routeNm = null;
        params.data.routeId = null;
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("route")}] ${t("MSG_InvalidValue")}` });
        return;
      }

      const routeCd = params.value?.routeCd;
      node.data.routeId = routeMap.current[routeCd].id;
      node.data.routeCd = routeMap.current[routeCd].routeCd;
      node.data.routeNm = routeMap.current[routeCd].name;
      node.data.itemId = routeMap.current[routeCd].item?.id;
      node.data.itemCd = routeMap.current[routeCd].item?.itemCd;
      node.data.itemNm = routeMap.current[routeCd].item?.name;

    } else if (isEqual(colId, "customerNm")) {
      const selectedName = params.value;
      const customerList = params?.context?.customers ?? customers;
      const customer = customerList.find(c => c.name === selectedName);
      if (customer) {
        params.data.customerId = customer.id;
        params.data.customerNm = customer.name;
        params.data.customerCd = customer.customerCd;
      } else {
        params.data.customerNm = null;
        params.data.customerCd = null;
      }
    }
    DataState.setDataState(params);
  }

  const setGridData = results => {
    const gridData = [];
    if (!isEmpty(results)) {
      for (const res of results) {
        const data = { ...res };
        if (res.item) {
          data.itemId = res.item.id;
          data.itemCd = res.item.itemCd;
          data.itemNm = res.item.name;
        }
        if (res.location) {
          data.locationId = res.location.id;
          data.locationCd = res.location.locCd;
          data.locationNm = res.location.name;
        }
        if (res.resource) {
          data.resourceId = res.resource.id;
          data.resourceCd = res.resource.resCd;
          data.resourceNm = res.resource.name;
        }
        if (res.workCenter) {
          data.workCenterId = res.workCenter.id;
          data.workCenterCd = res.workCenter.wcCd;
        }
        if (res.route) {
          data.routeId = res.route.id;
          data.routeCd = res.route.routeCd;
          data.routeNm = res.route.name;
        }
        if (res.customer) {
          data.customerCd = res.customer.customerCd;
          data.customerNm = res.customer.name;
        }
        gridData.push(data);
      }
    }
    return gridData;
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveWip();
    }
  }

  const removeWip = () => {
    const wipId = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        wipId.push(data.id)
      }
    );

    if (wipId.length > 0) {
      wipService.remove(wipId)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveWip();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }

  }

  const saveWip = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      let hasError = false;

      for (const row of allStateData) {
        if (row.resourceCd === null) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("resource")}] ${t("MSG_FieldRequired")}` });
          hasError = true;
          break;
        }
        if (row.prodDate === null) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("prodDate")}] ${t("MSG_FieldRequired")}` });
          hasError = true;
          break;
        }
        if (row.itemCd === null) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("itemCd")}] ${t("MSG_FieldRequired")}` });
          hasError = true;
          break;
        }
        if (row.routeCd === null) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("route")}] ${t("MSG_FieldRequired")}` });
          hasError = true;
          break;
        }
        if (row.locationCd === null) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("locCd")}] ${t("MSG_FieldRequired")}` });
          hasError = true;
          break;
        }
        if (row.workCenterCd === null) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("workcenter")}] ${t("MSG_FieldRequired")}` });
          hasError = true;
          break;
        }
      }

      if (hasError) {
        return;
      }

      wipService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .then(() => {
          retrieveWip();
        });

    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveWip();
    } else if (action === "DELETE") {
      removeWip();
    }
  }

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!reduxViewState?.resourceOptions) {
      retrieveResource();
    } else {
      setResource(reduxViewState.resourceOptions);
    }

    if (!reduxViewState?.route) {
      retrieveRoute();
    } else {
      setRoute(reduxViewState.route);
    }

    if (!reduxViewState?.customers) {
      retrieveCustomers();
    } else {
      setCustomers(reduxViewState.customers);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setWip([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  useEffect(() => {
    if (gridLoading) {
      retrieveWip();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, resourceSearch, date]);

  return (
    <>
      <FilterContainer>
        <Box sx={{ display: "flex", mr: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t("periodStart")}
              value={date}
              format={"yyyy-MM-dd"}
              views={["year", "month", "day"]}
              openTo={"day"}
              onAccept={newStartValue => {
                setDate(newStartValue);
                reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, date: newStartValue }));
              }}
              slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
            />
          </LocalizationProvider>
        </Box>
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <Autocomplete
          options={resource}
          getOptionLabel={option => option.name}
          value={resourceSearch}
          onChange={(_, newValue) => setResourceSearch(newValue)}
          isOptionEqualToValue={(option, value) => option?.resCd === value?.resCd}
          size="small"
          sx={{ pr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("resource")} />
          )}
        />
        <MPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} extraCodesRef={extraItemCodesRef} key={"ITEM"} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={wipService} params={getRequestParams()} columnDefs={defaultExportColumnDefs} setExcelData={setGridData} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area non-background-selected-row" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={wip}
            {...defaultGridMemo}
            rowSelection={{
              ...defaultGridMemo.rowSelection,
              headerCheckbox: true,
              checkboxes: true,
            }}
            onGridReady={onGridReady}
            onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            statusBar={statusBar}
            onCellValueChanged={onCellValueChanged}
            context={
              { resource: reduxViewState?.resourceOptions ?? resource,
                route: reduxViewState?.route ?? route,
                customers: reduxViewState?.customers ?? customers
              }
            }
            groupDisplayType={'custom'}
            groupDefaultExpanded={12}
            groupHideOpenParents
          >
          </AgGridReact>
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(WIP);
